import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { motion } from 'framer-motion';
import { ArrowRight, FileText } from 'lucide-react';
import { alaViewport } from './alaMotion';

const featuredCtaDef: SlotGroupDefinition = {
  label: 'Featured Insight CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue:
        'https://irp.cdn-website.com/dac7e7d5/files/uploaded/American+Landmark+-+ALA+Insights+-+2Q+2025+Sun+Belt+Market+Report.pdf',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Learn More' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const insight2CtaDef: SlotGroupDefinition = {
  label: 'Insight 2 CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue:
        'https://irp.cdn-website.com/dac7e7d5/files/uploaded/American_Landmark_-_ALA_Insights_-_1Q_2025_U.S._Multifamily_Market_Conditions.pdf',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Learn More' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const insight3CtaDef: SlotGroupDefinition = {
  label: 'Insight 3 CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/our-impact',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Learn More' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const insight4CtaDef: SlotGroupDefinition = {
  label: 'Insight 4 CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue:
        'https://irp.cdn-website.com/dac7e7d5/files/uploaded/ALA_Insights_-_U.S._Multifamily_Market_Conditions_October_2024.pdf',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Learn More' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const investorsCtaDef: SlotGroupDefinition = {
  label: 'Investors CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/investors' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Invest With Us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const contactCtaDef: SlotGroupDefinition = {
  label: 'Contact CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Contact Capital Markets' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function EyebrowDot({ light = false }: { light?: boolean }) {
  return (
    <span
      className={`h-[9px] min-h-[9px] w-[9px] shrink-0 rounded-full ${light ? 'bg-[rgb(var(--ala-accent))]' : 'bg-[rgb(var(--ala-accent))]'}`}
      aria-hidden
    />
  );
}

export function InsightsContent() {
  const featuredCta = useSlotGroup('insightscontent.featured_cta', featuredCtaDef);
  const insight2Cta = useSlotGroup('insightscontent.insight_2_cta', insight2CtaDef);
  const insight3Cta = useSlotGroup('insightscontent.insight_3_cta', insight3CtaDef);
  const insight4Cta = useSlotGroup('insightscontent.insight_4_cta', insight4CtaDef);
  const investorsCta = useSlotGroup('insightscontent.investors_cta', investorsCtaDef);
  const contactCta = useSlotGroup('insightscontent.contact_cta', contactCtaDef);

  return (
    <div className="w-full bg-[rgb(var(--ala-background))]">
      {/* Intro */}
      <section className="w-full overflow-clip bg-[rgb(var(--ala-background))] py-20 md:py-28">
        <div className="mx-auto max-w-[1440px] px-6 md:px-[clamp(16px,6vw,87px)]">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.7 }}
            className="max-w-[760px]"
          >
            <div className="mb-4 flex items-center gap-3">
              <EyebrowDot />
              <RichTextContent
                path="insightscontent.intro_eyebrow"
                label="Intro Eyebrow"
                as="span"
                className="font-heading text-base font-medium uppercase tracking-[2px] text-[rgb(var(--ala-dark))]/60"
              />
            </div>
            <RichTextContent
              path="insightscontent.intro_headline"
              label="Intro Headline"
              as="h2"
              className="font-heading text-[clamp(28px,3.2vw,48px)] font-medium leading-[1.1] tracking-[-0.03em] text-[rgb(var(--ala-dark))]"
            />
            <RichTextContent
              path="insightscontent.intro_body"
              label="Intro Body"
              as="div"
              className="mt-6 font-heading text-base font-normal leading-relaxed text-[rgb(var(--ala-dark))]/70 [&_p]:mb-4 [&_p:last-child]:mb-0"
            />
          </motion.div>
        </div>
      </section>

      {/* Featured insight */}
      <section className="w-full overflow-clip bg-[rgb(var(--ala-dark))] py-20 md:py-32">
        <div className="mx-auto max-w-[1440px] px-6 md:px-[clamp(16px,6vw,87px)]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.7 }}
            className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[1fr_auto] lg:gap-16"
          >
            <div className="flex flex-col">
              <div className="mb-4 flex items-center gap-3">
                <EyebrowDot light />
                <RichTextContent
                  path="insightscontent.featured_eyebrow"
                  label="Featured Eyebrow"
                  as="span"
                  className="font-heading text-base font-medium uppercase tracking-[2px] text-[rgb(var(--ala-on-primary))]/70"
                />
              </div>
              <RichTextContent
                path="insightscontent.featured_date"
                label="Featured Date"
                as="span"
                className="mb-3 font-heading text-sm font-semibold uppercase tracking-[0.16em] text-[rgb(var(--ala-secondary))]"
              />
              <RichTextContent
                path="insightscontent.featured_title"
                label="Featured Title"
                as="h2"
                className="max-w-[18ch] font-heading text-[clamp(28px,3.2vw,48px)] font-medium leading-[1.1] tracking-[-0.03em] text-[rgb(var(--ala-on-primary))]"
              />
              <RichTextContent
                path="insightscontent.featured_body"
                label="Featured Body"
                as="div"
                className="mt-6 max-w-[640px] font-heading text-base font-normal leading-relaxed text-[rgb(var(--ala-on-primary))]/75 [&_p]:mb-4 [&_p:last-child]:mb-0"
              />
              <EditableSlotGroup
                groupPath="insightscontent.featured_cta"
                definition={featuredCtaDef}
              >
                <a
                  href={
                    featuredCta.url ||
                    'https://irp.cdn-website.com/dac7e7d5/files/uploaded/American+Landmark+-+ALA+Insights+-+2Q+2025+Sun+Belt+Market+Report.pdf'
                  }
                  target={featuredCta._target || '_blank'}
                  rel={
                    featuredCta._target === '_blank' ? 'noopener noreferrer' : undefined
                  }
                  className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-[rgb(var(--ala-accent))] px-7 py-4 font-heading text-sm font-semibold uppercase tracking-[-0.3px] text-[rgb(var(--ala-on-primary))] transition-colors hover:bg-[#B80022]"
                >
                  {featuredCta.label || 'Learn More'}
                  <ArrowRight className="h-4 w-4" />
                </a>
              </EditableSlotGroup>
            </div>

            <div
              className="hidden h-[280px] w-[220px] flex-col items-center justify-center gap-4 rounded-xl border border-white/10 bg-white/[0.04] lg:flex"
              aria-hidden
            >
              <FileText className="h-16 w-16 text-white" />
              <span className="font-heading text-xs font-semibold uppercase tracking-[0.18em] text-white">
                Market Report
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Archive */}
      <section className="w-full overflow-clip bg-[#F7F7F7] py-20 md:py-32">
        <div className="mx-auto max-w-[1440px] px-6 md:px-[clamp(16px,6vw,87px)]">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.7 }}
            className="mb-12 max-w-[720px] md:mb-16"
          >
            <div className="mb-4 flex items-center gap-3">
              <EyebrowDot />
              <RichTextContent
                path="insightscontent.archive_eyebrow"
                label="Archive Eyebrow"
                as="span"
                className="font-heading text-base font-medium uppercase tracking-[2px] text-[rgb(var(--ala-dark))]/60"
              />
            </div>
            <RichTextContent
              path="insightscontent.archive_headline"
              label="Archive Headline"
              as="h2"
              className="font-heading text-[clamp(28px,3.2vw,48px)] font-medium leading-[1.1] tracking-[-0.03em] text-[rgb(var(--ala-dark))]"
            />
          </motion.div>

          <div className="flex flex-col">
            <motion.article
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={alaViewport}
              transition={{ duration: 0.6 }}
              className="border-t border-[rgb(var(--ala-dark))]/[0.12] py-10 md:py-12"
            >
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-[160px_1fr] lg:gap-12">
                <RichTextContent
                  path="insightscontent.insight_2_date"
                  label="Insight 2 Date"
                  as="span"
                  className="font-heading text-sm font-semibold uppercase tracking-[0.16em] text-[rgb(var(--ala-secondary))]"
                />
                <div className="max-w-[760px]">
                  <RichTextContent
                    path="insightscontent.insight_2_title"
                    label="Insight 2 Title"
                    as="h3"
                    className="font-heading text-[clamp(22px,2.2vw,32px)] font-medium leading-[1.2] tracking-[-0.02em] text-[rgb(var(--ala-dark))]"
                  />
                  <RichTextContent
                    path="insightscontent.insight_2_body"
                    label="Insight 2 Body"
                    as="div"
                    className="mt-4 font-heading text-base font-normal leading-relaxed text-[rgb(var(--ala-dark))]/70 [&_p]:mb-4 [&_p:last-child]:mb-0"
                  />
                  <EditableSlotGroup
                    groupPath="insightscontent.insight_2_cta"
                    definition={insight2CtaDef}
                  >
                    <a
                      href={
                        insight2Cta.url ||
                        'https://irp.cdn-website.com/dac7e7d5/files/uploaded/American_Landmark_-_ALA_Insights_-_1Q_2025_U.S._Multifamily_Market_Conditions.pdf'
                      }
                      target={insight2Cta._target || '_blank'}
                      rel={
                        insight2Cta._target === '_blank'
                          ? 'noopener noreferrer'
                          : undefined
                      }
                      className="mt-6 inline-flex w-fit items-center gap-2 font-heading text-sm font-semibold uppercase tracking-[0.08em] text-[rgb(var(--ala-dark))] transition-colors hover:text-[rgb(var(--ala-accent))]"
                    >
                      {insight2Cta.label || 'Learn More'}
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </EditableSlotGroup>
                </div>
              </div>
            </motion.article>

            <motion.article
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={alaViewport}
              transition={{ duration: 0.6, delay: 0.08 }}
              className="border-t border-[rgb(var(--ala-dark))]/[0.12] py-10 md:py-12"
            >
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-[160px_1fr] lg:gap-12">
                <RichTextContent
                  path="insightscontent.insight_3_date"
                  label="Insight 3 Date"
                  as="span"
                  className="font-heading text-sm font-semibold uppercase tracking-[0.16em] text-[rgb(var(--ala-secondary))]"
                />
                <div className="max-w-[760px]">
                  <RichTextContent
                    path="insightscontent.insight_3_title"
                    label="Insight 3 Title"
                    as="h3"
                    className="font-heading text-[clamp(22px,2.2vw,32px)] font-medium leading-[1.2] tracking-[-0.02em] text-[rgb(var(--ala-dark))]"
                  />
                  <RichTextContent
                    path="insightscontent.insight_3_body"
                    label="Insight 3 Body"
                    as="div"
                    className="mt-4 font-heading text-base font-normal leading-relaxed text-[rgb(var(--ala-dark))]/70 [&_p]:mb-4 [&_p:last-child]:mb-0"
                  />
                  <EditableSlotGroup
                    groupPath="insightscontent.insight_3_cta"
                    definition={insight3CtaDef}
                  >
                    <a
                      href={insight3Cta.url || '/our-impact'}
                      target={insight3Cta._target || '_self'}
                      rel={
                        insight3Cta._target === '_blank'
                          ? 'noopener noreferrer'
                          : undefined
                      }
                      className="mt-6 inline-flex w-fit items-center gap-2 font-heading text-sm font-semibold uppercase tracking-[0.08em] text-[rgb(var(--ala-dark))] transition-colors hover:text-[rgb(var(--ala-accent))]"
                    >
                      {insight3Cta.label || 'Learn More'}
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </EditableSlotGroup>
                </div>
              </div>
            </motion.article>

            <motion.article
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={alaViewport}
              transition={{ duration: 0.6, delay: 0.16 }}
              className="border-t border-b border-[rgb(var(--ala-dark))]/[0.12] py-10 md:py-12"
            >
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-[160px_1fr] lg:gap-12">
                <RichTextContent
                  path="insightscontent.insight_4_date"
                  label="Insight 4 Date"
                  as="span"
                  className="font-heading text-sm font-semibold uppercase tracking-[0.16em] text-[rgb(var(--ala-secondary))]"
                />
                <div className="max-w-[760px]">
                  <RichTextContent
                    path="insightscontent.insight_4_title"
                    label="Insight 4 Title"
                    as="h3"
                    className="font-heading text-[clamp(22px,2.2vw,32px)] font-medium leading-[1.2] tracking-[-0.02em] text-[rgb(var(--ala-dark))]"
                  />
                  <RichTextContent
                    path="insightscontent.insight_4_body"
                    label="Insight 4 Body"
                    as="div"
                    className="mt-4 font-heading text-base font-normal leading-relaxed text-[rgb(var(--ala-dark))]/70 [&_p]:mb-4 [&_p:last-child]:mb-0"
                  />
                  <EditableSlotGroup
                    groupPath="insightscontent.insight_4_cta"
                    definition={insight4CtaDef}
                  >
                    <a
                      href={
                        insight4Cta.url ||
                        'https://irp.cdn-website.com/dac7e7d5/files/uploaded/ALA_Insights_-_U.S._Multifamily_Market_Conditions_October_2024.pdf'
                      }
                      target={insight4Cta._target || '_blank'}
                      rel={
                        insight4Cta._target === '_blank'
                          ? 'noopener noreferrer'
                          : undefined
                      }
                      className="mt-6 inline-flex w-fit items-center gap-2 font-heading text-sm font-semibold uppercase tracking-[0.08em] text-[rgb(var(--ala-dark))] transition-colors hover:text-[rgb(var(--ala-accent))]"
                    >
                      {insight4Cta.label || 'Learn More'}
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </EditableSlotGroup>
                </div>
              </div>
            </motion.article>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="w-full overflow-clip bg-[rgb(var(--ala-dark))] py-20 md:py-28">
        <div className="mx-auto max-w-[1440px] px-6 md:px-[clamp(16px,6vw,87px)]">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.7 }}
            className="max-w-[720px]"
          >
            <div className="mb-4 flex items-center gap-3">
              <EyebrowDot light />
              <RichTextContent
                path="insightscontent.cta_eyebrow"
                label="CTA Eyebrow"
                as="span"
                className="font-heading text-base font-medium uppercase tracking-[2px] text-[rgb(var(--ala-on-primary))]/70"
              />
            </div>
            <RichTextContent
              path="insightscontent.cta_headline"
              label="CTA Headline"
              as="h2"
              className="font-heading text-[clamp(28px,3.2vw,48px)] font-medium leading-[1.1] tracking-[-0.03em] text-[rgb(var(--ala-on-primary))]"
            />
            <RichTextContent
              path="insightscontent.cta_body"
              label="CTA Body"
              as="div"
              className="mt-6 font-heading text-base font-normal leading-relaxed text-[rgb(var(--ala-on-primary))]/75 [&_p]:mb-4 [&_p:last-child]:mb-0"
            />
            <div className="mt-8 flex flex-wrap gap-4">
              <EditableSlotGroup
                groupPath="insightscontent.investors_cta"
                definition={investorsCtaDef}
              >
                <a
                  href={investorsCta.url || '/investors'}
                  target={investorsCta._target || '_self'}
                  rel={
                    investorsCta._target === '_blank' ? 'noopener noreferrer' : undefined
                  }
                  className="inline-flex items-center gap-2 rounded-full bg-[rgb(var(--ala-accent))] px-7 py-4 font-heading text-sm font-semibold uppercase tracking-[-0.3px] text-[rgb(var(--ala-on-primary))] transition-colors hover:bg-[#B80022]"
                >
                  {investorsCta.label || 'Invest With Us'}
                  <ArrowRight className="h-4 w-4" />
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup
                groupPath="insightscontent.contact_cta"
                definition={contactCtaDef}
              >
                <a
                  href={contactCta.url || '/contact-us'}
                  target={contactCta._target || '_self'}
                  rel={contactCta._target === '_blank' ? 'noopener noreferrer' : undefined}
                  className="inline-flex items-center gap-2 rounded-full border border-[rgb(var(--ala-on-primary))]/30 px-7 py-4 font-heading text-sm font-semibold uppercase tracking-[-0.3px] text-[rgb(var(--ala-on-primary))] transition-colors hover:border-[rgb(var(--ala-on-primary))] hover:bg-[rgb(var(--ala-on-primary))]/5"
                >
                  {contactCta.label || 'Contact Capital Markets'}
                  <ArrowRight className="h-4 w-4" />
                </a>
              </EditableSlotGroup>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'InsightsContent',
  displayName: 'Insights Content',
  tokens: {
    'insightscontent.intro_eyebrow': {
      label: 'Intro Eyebrow',
      type: 'richtext',
      defaultValue: '<p>market intelligence</p>',
    },
    'insightscontent.intro_headline': {
      label: 'Intro Headline',
      type: 'richtext',
      defaultValue: '<p>Research and perspective from the American Landmark platform.</p>',
    },
    'insightscontent.intro_body': {
      label: 'Intro Body',
      type: 'richtext',
      defaultValue:
        '<p>ALA Insights shares our view on U.S. multifamily conditions — market fundamentals, capital trends, and the people-first operating practices that shape performance across the Sun Belt.</p>',
    },
    'insightscontent.featured_eyebrow': {
      label: 'Featured Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Latest Report</p>',
    },
    'insightscontent.featured_date': {
      label: 'Featured Date',
      type: 'richtext',
      defaultValue: '<p>July 2025</p>',
    },
    'insightscontent.featured_title': {
      label: 'Featured Title',
      type: 'richtext',
      defaultValue: '<p>ALA Insights: 2Q 2025 U.S. Sun Belt Multifamily Market Report</p>',
    },
    'insightscontent.featured_body': {
      label: 'Featured Body',
      type: 'richtext',
      defaultValue:
        '<p>Market fundamentals are improving with cap rates stabilizing, rent growth turning positive, and construction starts declining sharply. The South continues to be supported by robust demographic and job growth, and southern markets have large property segments that are ripe for value-add strategies. While continued high interest rates are creating financial stress for some property owners, they are creating attractive investment opportunities for others. As the market continues to stabilize and we enter this next cycle, the experienced operators with buying power and access to off-market opportunities in high growth markets should prevail.</p>',
    },
    'insightscontent.archive_eyebrow': {
      label: 'Archive Eyebrow',
      type: 'richtext',
      defaultValue: '<p>archive</p>',
    },
    'insightscontent.archive_headline': {
      label: 'Archive Headline',
      type: 'richtext',
      defaultValue: '<p>Previous insights</p>',
    },
    'insightscontent.insight_2_date': {
      label: 'Insight 2 Date',
      type: 'richtext',
      defaultValue: '<p>May 2025</p>',
    },
    'insightscontent.insight_2_title': {
      label: 'Insight 2 Title',
      type: 'richtext',
      defaultValue: '<p>ALA Insights: 1Q 2025 U.S. Multifamily Market Conditions</p>',
    },
    'insightscontent.insight_2_body': {
      label: 'Insight 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>The U.S. multifamily market continues to navigate an evolving economic landscape characterized by a resilient labor market, moderate inflation, and shifting investment dynamics. While single-family affordability challenges persist, rental demand remains robust, supported by limited homeownership accessibility and demographic tailwinds, particularly in the Sun Belt. These factors are shaping the market’s trajectory in 2025, influencing everything from capital flows to construction activity.</p>',
    },
    'insightscontent.insight_3_date': {
      label: 'Insight 3 Date',
      type: 'richtext',
      defaultValue: '<p>February 2025</p>',
    },
    'insightscontent.insight_3_title': {
      label: 'Insight 3 Title',
      type: 'richtext',
      defaultValue: '<p>ALA Insights: The Vital Role of People in Multifamily Investments</p>',
    },
    'insightscontent.insight_3_body': {
      label: 'Insight 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>“As corporate DEI programs face challenges in today’s polarized political environment, organizations are struggling to find the right balance. At American Landmark we believe that, when implemented appropriately, DEI initiatives are key to supporting our residents, teams, and communities. To achieve our mission to create healthy, equitable, and sustainable environments, we are committed to sustainable practices and social responsibility initiatives that we believe have the potential to enhance investment performance, drive operational efficiency, increase resident satisfaction, and improve team member experiences and productivity.”</p>',
    },
    'insightscontent.insight_4_date': {
      label: 'Insight 4 Date',
      type: 'richtext',
      defaultValue: '<p>October 2024</p>',
    },
    'insightscontent.insight_4_title': {
      label: 'Insight 4 Title',
      type: 'richtext',
      defaultValue: '<p>ALA Insights: U.S. Multifamily Market Conditions</p>',
    },
    'insightscontent.insight_4_body': {
      label: 'Insight 4 Body',
      type: 'richtext',
      defaultValue:
        '<p>The multifamily market is beginning to show signs of stabilization after a period marked by an unprecedented acceleration in interest rates increases and excess supply following a demand spike during the pandemic. Looking ahead to 2025 and beyond, potential economic shifts, rate cuts, supply absorption, and ongoing technological advancements suggest a favorable outlook and are paving the way for renewed growth and stability in the multifamily sector.</p>',
    },
    'insightscontent.cta_eyebrow': {
      label: 'CTA Eyebrow',
      type: 'richtext',
      defaultValue: '<p>next step</p>',
    },
    'insightscontent.cta_headline': {
      label: 'CTA Headline',
      type: 'richtext',
      defaultValue: '<p>Ready to put insight into action?</p>',
    },
    'insightscontent.cta_body': {
      label: 'CTA Body',
      type: 'richtext',
      defaultValue:
        '<p>Explore our investment platform or connect with our capital markets team to discuss opportunities across high-growth Sun Belt markets.</p>',
    },
  },
  slotGroups: {
    'insightscontent.featured_cta': featuredCtaDef,
    'insightscontent.insight_2_cta': insight2CtaDef,
    'insightscontent.insight_3_cta': insight3CtaDef,
    'insightscontent.insight_4_cta': insight4CtaDef,
    'insightscontent.investors_cta': investorsCtaDef,
    'insightscontent.contact_cta': contactCtaDef,
  },
};

(InsightsContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default InsightsContent;
